import React, { useState } from 'react';
import {
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Typography,
	Button,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Fade,
	Badge,
	Box
} from '@material-ui/core';
import {
	ExpandMore,
	Tune
} from '@material-ui/icons';
import { makeStyles, useTheme } from '@material-ui/core/styles';

import Sort from '../../sort/Sort';
import DateRangeSelector from '../../../../DateRangeSelector';

import UIHelper from '../../../../../common/UIHelper';

const useStyles = makeStyles((theme) => ({
	heading: {
		fontSize: theme.typography.pxToRem(15),
		flexBasis: '33.33%',
		flexShrink: 0,
	},
	secondaryHeading: {
		fontSize: theme.typography.pxToRem(15),
		color: theme.palette.text.secondary,
	},
}));

export default function SidebarPAFilter(props) {
	const {
		displayAttribute,
		filterAttributes,
		customAttributeMap,
		filterValues,
		currency,
		width,
		productAttributes,
		setCustomAttributeMap,
		resetFilter
	} = props;
	const classes = useStyles();
	const [showModal, setShowModal] = useState(false);
	const theme = useTheme();

	const customAttributes = productAttributes && productAttributes.customAttributes
		? productAttributes.customAttributes
		: [];
	const standardAttributes = productAttributes && productAttributes.standardAttributes
		? productAttributes.standardAttributes
		: [];
	const badgeFilterValues = filterValues
		? Object.fromEntries(Object.entries(filterValues).filter(([value]) => value !== null))
		: {};
	const badgeFilterAttributes = filterAttributes && !filterAttributes[0] ? filterAttributes : {};

	const toggleModal = () => {
		setShowModal(!showModal);
	};

	const ModalBody = standardAttributes.concat(customAttributes).map((attribute) => {
		if (attribute && attribute.isFiltered) {
			const {
				uniqueName, name, pickListElements
			} = attribute;
			if (uniqueName === 'location' || (name === 'Category' && pickListElements && !pickListElements.length)) {
				return null;
			}
			return (
				<div style={{ marginBottom: '10px' }} data-cy={attribute.name}>
					{attribute.name}
					:
					{' '}
					{displayAttribute(attribute)}
				</div>
			);
		}
		return null;
	});

	const displaySidebarAttribute = (attribute) => {
		const key = customAttributeMap.get(attribute.uniqueName)
			? customAttributeMap.get(attribute.uniqueName)
			: attribute.uniqueName;
		let buttonLabel = '';
		if (filterAttributes[key]) {
			if (attribute.attributeType === 'Multi_Picklist') {
				buttonLabel = '';
				if (filterAttributes[key] && filterAttributes[key].searchValue) {
					buttonLabel += filterAttributes[key].searchValue.map((selectedObj) => ` ${selectedObj.label}`);
				} else {
					buttonLabel += filterAttributes[key].map((selectedObj) => selectedObj.label);
				}
			} else if (attribute.attributeType === 'Picklist') {
				buttonLabel = filterAttributes[key];
			}
		}
		if (filterValues[`${attribute.uniqueName}filterValue`]) {
			if (attribute.attributeType === 'Currency') {
				buttonLabel = `${UIHelper.currencyFormatter(currency || 'cad')
					.format(filterValues[`${attribute.uniqueName}filterValue`][0])} - 
				${UIHelper.currencyFormatter(currency || 'cad')
		.format(filterValues[`${attribute.uniqueName}filterValue`][1])}`;
			} else {
				buttonLabel = `${filterValues[`${attribute.uniqueName}filterValue`][0]} - 
                ${filterValues[`${attribute.uniqueName}filterValue`][1]}`;
			}
		}
		if (attribute.uniqueName === 'location'
        || (attribute.name === 'Category'
        && attribute.pickListElements
        && !attribute.pickListElements.length)) {
			return null;
		}
		return (
			<Accordion defaultExpanded>
				<AccordionSummary
					expandIcon={<ExpandMore />}
					aria-controls="panel1a-content"
					id="panel1a-header"
				>
					<Typography className={classes.heading}>
						{attribute.name}
					</Typography>
					<Typography className={classes.secondaryHeading} data-cy={buttonLabel}>
						{buttonLabel}
					</Typography>
				</AccordionSummary>
				<AccordionDetails data-cy={attribute.name}>
					{displayAttribute(attribute)}
				</AccordionDetails>
			</Accordion>
		);
	};

	const finalDiv = [];

	if (productAttributes && productAttributes.standardAttributes && productAttributes.standardAttributes.length) {
		finalDiv.push(productAttributes.standardAttributes.map((standardAttribute, index) => (
			standardAttribute.isFiltered ? displaySidebarAttribute(standardAttribute, index + 100) : null
		)));
	}

	if (productAttributes && productAttributes.customAttributes && productAttributes.customAttributes.length) {
		setCustomAttributeMap();
		const customAttr = (productAttributes.customAttributes.map((customAttribute, index) => {
			setCustomAttributeMap(customAttribute);
			return customAttribute.isFiltered ? displaySidebarAttribute(customAttribute, index) : null;
		}));
		finalDiv.push(customAttr);
	}

	return (
		width === 'xs'
			? (
				<>
					<Badge
						badgeContent={Object.keys(badgeFilterValues).length + Object.keys(badgeFilterAttributes).length}
						color="secondary"
						overlap="circular"
					>
						<IconButton
							color="inherit"
							aria-label="open filters"
							edge="start"
							onClick={toggleModal}
							style={{ outline: 0, padding: '5px', margin: '0px' }}
							data-cy="mobileFilterButton"
						>
							<Tune
								style={{ fontSize: '30px' }}
								color={Object.keys(badgeFilterValues).length
                            || Object.keys(badgeFilterAttributes).length
									? 'secondary'
									: 'primary'}
							/>
						</IconButton>
					</Badge>
					<Dialog
						open={showModal}
						onClose={toggleModal}
						PaperProps={{ style: { borderRadius: 10, minWidth: '300px', padding: '20px' } }}
						transitionComponent={Fade}
						scroll="paper"
					>
						<DialogTitle style={{ padding: '0px 8px' }}>Filters</DialogTitle>
						<DialogContent style={{ padding: '8px' }}>
							{width === 'xs'
								? (
									<Box
										display="flex"
										style={
											{ justifyContent: 'space-between', marginBottom: '10px', alignItems: 'center' }
										}
									>
										<DateRangeSelector
											callback={props.callback}
											pageActiveCallback={props.pageActiveCallback}
											searchParams={props.searchParams}
											showTriggerButton={props.showTriggerButton}
											searchComponents={props.searchComponents}
										/>
										<Sort {...props} />
									</Box>
								)
								: null}
							{ ModalBody }
						</DialogContent>
						<DialogActions disableSpacing style={{ justifyContent: 'space-around' }}>
							<Button variant="contained" color="primary" onClick={toggleModal}>Close</Button>
							<Button
								variant="contained"
								style={{ color: 'white', backgroundColor: theme.palette.error.main }}
								onClick={() => { resetFilter(); toggleModal(); }}
							>
								Clear All
							</Button>
						</DialogActions>
					</Dialog>
				</>
			)
			: finalDiv
	);
}
